import {
  Transformer,
  builtInPlugins,
  type ITransformPlugin,
} from 'markmap-lib';

/**
 * Shared markdown parsing for the mindmap artifact.
 *
 * Extracted from `MindmapRenderer` so the radial renderer can reuse it rather
 * than carry a second copy of the regex. Both renderers feed the same
 * markmap-lib `Transformer`, so they must strip the same things.
 */

const FENCED_MARKDOWN_RE = /^\s*```(?:markdown|md)?\s*\n([\s\S]*?)\n```\s*$/i;

/**
 * Some LLM runs wrap the whole markdown response in a ```markdown ... ```
 * fence. markmap-lib then sees one giant code block and renders the literal
 * text instead of nodes, so the fence has to come off before transforming.
 * Old artifacts already stored that way render correctly too.
 */
export function stripMarkdownCodefence(markdown: string): string {
  const match = markdown.trim().match(FENCED_MARKDOWN_RE);
  return match ? match[1].trim() : markdown;
}

/**
 * markmap's `node.content` is HTML, not text - inline markdown is already
 * converted, so `**Bold**` arrives as `<strong>Bold</strong>`. Renderers that
 * paint labels into SVG `<text>` or plain HTML need the text, and injecting the
 * HTML would mean trusting model output. Emphasis is lost; that is the trade.
 */
export function nodeLabel(content: string): string {
  return decodeEntities(content.replace(/<[^>]+>/g, '')).trim();
}

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/** Named (`&apos;`) and numeric (`&#x2013;`, `&#39;`) entities to text. */
function decodeEntities(text: string): string {
  return text.replace(
    /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi,
    (whole, code: string) => {
      if (code[0] !== '#') return ENTITIES[code.toLowerCase()] ?? whole;
      const n =
        code[1].toLowerCase() === 'x'
          ? parseInt(code.slice(2), 16)
          : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : whole;
    }
  );
}

/**
 * Inline maths in a label: `$..$` (no space just inside either `$`, so
 * "costs $5 and $10" stays text) or `$$..$$`. One pattern for the parser, the
 * truncation and the concept key, so they agree on what is maths.
 */
const MATH_PATTERN = String.raw`\$\$[^$]+?\$\$|\$[^\s$](?:[^$\n]*?[^\s$])?\$`;
const MATH_RE = new RegExp(MATH_PATTERN, 'g');
const MATH_ONLY_RE = new RegExp(`^(?:${MATH_PATTERN})$`);
const MATH_AT_RE = new RegExp(`^(?:${MATH_PATTERN})`);
const LABEL_PARTS_RE = new RegExp(`(${MATH_PATTERN}|\\s+)`);

/** Does a label hold any inline maths (`$..$`)? */
export function hasMath(text: string): boolean {
  return new RegExp(MATH_PATTERN).test(text);
}

/** The few commands common in lesson headings, as KaTeX draws them. */
const TEX_SYMBOLS: Record<string, string> = {
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  Delta: 'Δ',
  theta: 'θ',
  lambda: 'λ',
  mu: 'μ',
  pi: 'π',
  sigma: 'σ',
  Sigma: 'Σ',
  omega: 'ω',
  Omega: 'Ω',
  times: '×',
  cdot: '·',
  pm: '±',
  le: '≤',
  leq: '≤',
  ge: '≥',
  geq: '≥',
  neq: '≠',
  approx: '≈',
  infty: '∞',
  to: '→',
  rightarrow: '→',
  sqrt: '√',
};

/**
 * Roughly the text KaTeX shows for one `$..$` span: delimiters off,
 * `\frac{a}{b}` as `a/b`, `^`/`_` groups as their content, common symbols as
 * their glyph, any other command dropped. For measuring and tooltips only -
 * the pill itself renders the real maths.
 */
export function visibleMath(tex: string): string {
  let out = tex.replace(/^\$\$?|\$\$?$/g, '');
  const frac = /\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g;
  while (frac.test(out)) out = out.replace(frac, '$1/$2');
  return out
    .replace(/\\([a-zA-Z]+)/g, (_, name: string) => TEX_SYMBOLS[name] ?? '')
    .replace(/\\./g, '')
    .replace(/[\^_{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A label as the reader sees it: every maths span reduced to its text. */
export function visibleLabel(label: string): string {
  return label.replace(MATH_RE, m => visibleMath(m));
}

/**
 * A label's words, the places a line may break: split at whitespace, but
 * never inside a `$..$` span, which is kept whole with any text touching it
 * (`($x$)` is one word). Raw TeX is kept; measure with `visibleLabel`.
 */
export function labelWords(label: string): string[] {
  const words: string[] = [];
  let word = '';
  for (const part of label.split(LABEL_PARTS_RE)) {
    if (!part) continue;
    if (/^\s+$/.test(part)) {
      if (word) words.push(word);
      word = '';
    } else {
      word += part;
    }
  }
  if (word) words.push(word);
  return words;
}

/**
 * markdown-it's `escape` and emphasis rules would eat a maths span's
 * backslashes and `*`s, so this rule claims `$..$` first and keeps it as raw
 * text. The pill renders it with KaTeX (`InlineMarkdown`).
 */
/** The part of markdown-it's inline state the rule uses. Typed here because
 *  `@types/markdown-it` is only a dev dependency of markmap-lib: Vercel's
 *  filtered install (`--filter ai-tutor-web...`) leaves it out, `md` turns
 *  `any` and the build fails on implicit-any parameters. */
interface InlineState {
  src: string;
  pos: number;
  push: (type: string, tag: string, nesting: -1 | 0 | 1) => { content: string };
}

const rawMathPlugin: ITransformPlugin = {
  name: 'rawMath',
  transform(hooks) {
    hooks.parser.tap(md => {
      md.inline.ruler.before(
        'escape',
        'raw_math',
        (state: InlineState, silent: boolean) => {
          if (state.src.charCodeAt(state.pos) !== 0x24) return false;
          const match = MATH_AT_RE.exec(state.src.slice(state.pos));
          if (!match) return false;
          if (!silent) state.push('text', '', 0).content = match[0];
          state.pos += match[0].length;
          return true;
        }
      );
    });
    return {};
  },
};

/**
 * markmap's plugins minus KaTeX. With KaTeX, `$\pi r^2$` arrived as KaTeX
 * HTML whose stripped text was the formula three times over; labels keep the
 * TeX instead, and the renderers draw it.
 */
const LABEL_PLUGINS: ITransformPlugin[] = [
  ...builtInPlugins.filter(p => p.name !== 'katex'),
  rawMathPlugin,
];

/**
 * One key for a concept name and the node label it became, so a node can be
 * matched to the lesson concept it names. The label went through markdown
 * (entities, emphasis, typographic quotes and dashes); the name did not.
 */
export function conceptKey(text: string): string {
  // Maths drops out on both sides: a name may spell it differently from the
  // heading the plan wrote (or not at all), and the words still match.
  return decodeEntities(text)
    .replace(MATH_RE, ' ')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[*_`]/g, '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

/** Plain-text truncation at the last word boundary inside the budget. */
function truncatePlain(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const clipped = text.slice(0, maxChars - 1).replace(/\s+\S*$/, '');
  return `${clipped || text.slice(0, maxChars - 1)}…`;
}

/**
 * Budget-based truncation, breaking at the last word boundary inside the
 * budget. Deliberately character-based rather than measured: measuring means
 * `getComputedTextLength()`, which means a post-render pass, which makes the
 * output depend on when it ran - and these renderers are snapshot-tested.
 *
 * The budget counts VISIBLE characters (`visibleLabel`), and a `$..$` span is
 * never cut: it is kept whole when it fits and dropped when it does not, since
 * half an equation renders as a TeX error.
 */
export function truncateLabel(text: string, maxChars: number): string {
  if (!hasMath(text)) return truncatePlain(text, maxChars);
  if (visibleLabel(text).length <= maxChars) return text;
  let out = '';
  let used = 0;
  for (const part of text.split(LABEL_PARTS_RE)) {
    if (!part) continue;
    const len = MATH_ONLY_RE.test(part)
      ? visibleMath(part).length
      : part.length;
    if (used + len > maxChars - 1) break;
    out += part;
    used += len;
  }
  out = out.trimEnd();
  if (out) return `${out}…`;
  // Not even the first word or equation fits: cut its visible text instead.
  return truncatePlain(visibleLabel(text), maxChars);
}

/**
 * One node of the parsed outline. Mirrors markmap's `IPureNode`, which
 * `markmap-lib` does not re-export - `content` is HTML, `children` is always
 * present on interior nodes.
 */
export interface MindmapNode {
  content: string;
  children?: MindmapNode[];
}

/**
 * Markdown -> outline tree, via markmap's own parser.
 *
 * Shared by every mindmap renderer so they all agree on what the outline IS:
 * the alternative is each view re-deriving heading/bullet nesting and
 * disagreeing at the edges. Returns null on a parse failure so callers can
 * render an empty state rather than crash.
 */
export function parseMindmapMarkdown(markdown: string): MindmapNode | null {
  try {
    const { root } = new Transformer(LABEL_PLUGINS).transform(
      stripMarkdownCodefence(markdown)
    );
    return root as MindmapNode;
  } catch {
    return null;
  }
}
